import { InMemoryCache, defaultDataIdFromObject } from "@apollo/client/cache";
import type { FieldMergeFunction, StoreObject } from "@apollo/client/cache";

const TypePerson = "Person";
const Membership = "Membership";
const IndividualAllocation = "IndividualAllocation";
const Import = "Import";

const merge: FieldMergeFunction = (_existing, incoming) => incoming;

const mergeShallow: FieldMergeFunction = (existing, incoming) => {
  if (!incoming) {
    return existing;
  }
  return { ...existing, ...incoming };
};

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // v4 equivalent of v3 cacheRedirects: resolves getPerson from the in-memory cache
        // when the entity was already loaded as part of a larger query (e.g. team members list),
        // avoiding a redundant network request. Returns undefined to fall back to network.
        getPerson: (_, { args, toReference, canRead }) => {
          if (!args?.["aggregateId"]) return undefined;
          const ref = toReference({
            __typename: TypePerson,
            aggregateId: args["aggregateId"],
          });
          return canRead(ref) ? ref : undefined;
        },
      },
    },
    Tag: {
      fields: {
        tagAttributesMetaData: {
          merge: mergeShallow,
        },
      },
    },
    LinkedStrategy: {
      keyFields: false,
    },
    Group: {
      fields: {
        tags: {
          merge,
        },
        permissions: {
          merge: mergeShallow,
        },
      },
    },
    ObjectiveAttribute: {
      keyFields: false,
    },
    GroupAttribute: {
      keyFields: false,
    },
    // Permissions objects have no ID — embed them in their parent to avoid
    // merge conflicts when different queries return different permission subsets.
    GroupPermissions: {
      keyFields: false,
    },
    PersonPermissions: {
      keyFields: false,
    },
    Person: {
      fields: {
        memberOf: {
          merge,
        },
        memberships: {
          merge,
        },
        allocations: {
          merge,
        },
        tags: {
          merge,
        },
        accessGroups: {
          merge,
        },
        permissions: {
          merge: mergeShallow,
        },
      },
    },
    Workspace: {
      fields: {
        config: {
          merge: mergeShallow,
        },
      },
    },
  },
  dataIdFromObject: (object: Readonly<StoreObject>) => {
    switch (object.__typename) {
      case Import:
        return `${Import}:${object["id"]}-${object["created"]}`;
      case TypePerson:
        return `${TypePerson}:${object["aggregateId"]}`;
      case IndividualAllocation:
        return `${IndividualAllocation}:${object["allocationId"]}`;
      case Membership:
        return `${Membership}:${object["currentCompositeId"]}`;
      default:
        return defaultDataIdFromObject(object);
    }
  },
});

export default cache;
